import type { ContractDocumentDownloadView, DocxIceAktarimKaybi } from "@closer/shared";
import { DOCX_ICE_AKTARIM_KAYIP_UYARILARI } from "@closer/shared";
import { Button, Card, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { uploadFile } from "../../lib/fileUpload";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ContractClausePanel } from "./ContractClausePanel";
import type { OtomasyonHost } from "./maddeAksiyonlari";

/**
 * SÖZLEŞME DOCX EDİTÖRÜ (CLOS-1252 — ADR-0070).
 *
 * ### ⚠ Motor SEÇİLDİ ama ölçülerek: `@docx-editor.dev/core` (Apache-2.0)
 *
 * ONLYOFFICE (3.500 USD) iptal edildi, Syncfusion ölçümle elendi (DOCX okumak için
 * .NET/Java servisi istiyor). Bu motor gerçek bir müvekkil sözleşmesiyle gerçek Chrome'da
 * ölçüldü: `w:ins` 14→14, `w:del` 12→12, tablo 6→6, yorum 4→4, 13 `word/` parçası birebir;
 * sayfalama Word ile aynı (9/9); **sunucu gerekmiyor**.
 *
 * ### ⚠ Editör KAYIT OTORİTESİ DEĞİL
 *
 * Yetki, versiyon, canonical artifact, hash ve denetim Closer'da kalıyor; editör bir
 * düzenleme yüzeyi. Motor değişse veri modeli değişmez — bu ONLYOFFICE silinirken pratikte
 * kanıtlandı (CLOS-1250): veri modeline hiç dokunulmadı.
 *
 * ### ⚠ MOD YETKİDEN TÜRETİLİYOR, ekranda sabitlenmiyor
 *
 * `contract.edit` → tam düzenleme · `contract.revision` → öneri · ikisi de yoksa
 * görüntüleme. Yetki sunucudan (`/context`) geliyor; ekranda sabitlenseydi konsoldan
 * `mode` değiştiren kullanıcı kendine düzenleme açardı.
 *
 * ⚠ Kapı burada TEKRARLANMIYOR: kaydetme yolu ayrı bir kartın işi ve asıl kapı orada,
 * sunucuda olacak. Buradaki mod bir kolaylık — yanlış modda açmak veri yazmıyor.
 *
 * ### ⚠ Öneri modu `w:ins` ÜRETMİYOR — ama artık fark KAYBOLMUYOR (CLOS-1255)
 *
 * Ölçüldü: ücretsiz katman revizyonları **koruyor ama üretmiyor**; `suggesting` modu
 * `editing`den farklı davranmıyor. Bu yüzden native redline kaydetme anında SUNUCUDA
 * üretiliyor: yüklenen paket baz versiyonla karşılaştırılıp `lib/docx/redline.ts` ile
 * `w:ins`/`w:del` yazılıyor (`modules/contracts/uploadRedline.ts`).
 *
 * ⚠ Mod yine de doğru veriliyor: kullanıcıya hangi yetkiyle çalıştığını söylüyor ve motor
 * ileride revizyon üretmeye başlarsa çağrı yeri doğru kalır.
 */

/**
 * ⚠ TEMBEL YÜKLEME ŞART (AC-5): çekirdek paketlenmiş hâlde ~3,3 MB. Statik import
 * olsaydı sözleşme listesine giren kullanıcı da o yükü indirirdi.
 */
const DocxEditor = lazy(async () => {
  const mod = await import("@docx-editor.dev/react");
  return { default: mod.DocxEditor };
});

/** ⚠ Motorun `onReady` ile verdiği tutamağın yalnız İHTİYACIMIZ OLAN yüzü. */
type EditorTutamagi = { save: () => Promise<ArrayBuffer | Uint8Array> };

/**
 * ⚠ OTOMASYON HOST'U DA TEMBEL: madde paneli motorun otomasyon protokolünü konuşuyor
 * (CLOS-1256) ve o protokol aynı 3,3 MB'lık çekirdekten geliyor. Statik import,
 * düzenleme yetkisi olmayan kullanıcıya da o yükü indirtirdi.
 */
async function otomasyonHostuKur(editor: unknown): Promise<OtomasyonHost | null> {
  const mod = await import("@docx-editor.dev/core/editor");
  const kur = (mod as { createBrowserAutomationHost?: (e: unknown) => unknown })
    .createBrowserAutomationHost;
  if (!kur) return null;
  return kur(editor) as OtomasyonHost;
}

export function ContractDocxEditor() {
  const { id, versionId } = useParams();
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const editorRef = useRef<EditorTutamagi | null>(null);
  /**
   * ⚠ Madde paneli motor HAZIR olmadan çizilemez: host bir editör örneği istiyor. `null`
   * iken panel hiç çizilmiyor — boş bir panel, belgede madde yokmuş gibi okunurdu.
   */
  const [otomasyon, setOtomasyon] = useState<OtomasyonHost | null>(null);

  const duzenleyebilir = can("contract.edit");
  const oneriYapabilir = can("contract.revision");
  /** ⚠ Sıra önemli: düzenleme yetkisi öneri yetkisini KAPSAR, tersi değil. */
  const mod = duzenleyebilir ? "edit" : oneriYapabilir ? "suggesting" : "view";

  /**
   * ⚠ Belge İMZALI URL'den geliyor, ham depolama yolundan değil. Yol tahmin edilseydi
   * kayıt erişimi kapısı atlanırdı — imzayı sunucu yalnız yetkili isteğe veriyor.
   */
  const indirme = useQuery({
    queryKey: tenantKey(tenantId, "contract", id ?? "", "canonical-docx", versionId ?? ""),
    enabled: Boolean(id && versionId),
    queryFn: async () => {
      const { downloadUrl } = (
        await api<ContractDocumentDownloadView>(
          `/api/contracts/${id}/versions/${versionId}/artifacts/canonical/download-url`,
          { tenantId },
        )
      ).data;

      /**
       * ⚠ BAYTLARI BİZ ÇEKİYORUZ, URL'i editöre VERMİYORUZ. İki sebep:
       *
       * 1. `DocumentSource` zaten URL kabul etmiyor (`ArrayBuffer | Uint8Array | 'blank'`).
       * 2. İmzalı URL kısa ömürlü; editöre verip onun ne zaman isteyeceğini beklemek,
       *    imzanın süresi dolduğunda sebebi belirsiz bir hataya dönüşürdü. Burada
       *    çekersek hata TANIDIK bir hata olur ve `StateView` onu anlatabilir.
       *
       * ⚠ `api()` KULLANILMIYOR: bu istek Closer API'sine değil imzalı depolama URL'ine
       * gidiyor; `x-tenant-id` ve zarf çözümü oraya ait değil.
       */
      const yanit = await fetch(downloadUrl);
      if (!yanit.ok) throw new Error("Sözleşme dosyası indirilemedi");
      return yanit.arrayBuffer();
    },
  });

  /**
   * KAYDETME (CLOS-1253).
   *
   * ### ⚠ YENİ UÇ AÇILMIYOR — mevcut `file_upload` yolu kullanılıyor
   *
   * `createContractVersionSchema` zaten `source: "file_upload"` + `storageObjectId`
   * taşıyor ve `POST /:id/versions` `contract.edit` kapısında, `idempotency()` ile korumalı.
   * Editöre özel bir uç açsaydık aynı kararın ikinci adresi doğar ve yetki/audit/idempotency
   * iki yerde ayrı bakım isterdi — şemanın kendi yorumunun yasakladığı şey.
   *
   * ⚠ `uploadFile` taramanın bitmesini de bekliyor: taranmamış bir dosya versiyona
   * bağlanamaz (ADR-0010 D2).
   */
  const kaydet = useMutation({
    mutationFn: async () => {
      const tutamak = editorRef.current;
      if (!tutamak) throw new Error("Editör henüz hazır değil");

      const baytlar = await tutamak.save();
      const dosya = new File([baytlar as BlobPart], `sozlesme-v${Date.now()}.docx`, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const yuklenen = await uploadFile({
        file: dosya,
        domain: "contract",
        entityId: id!,
        tenantId,
      });

      return (
        await api(`/api/contracts/${id}/versions`, {
          method: "POST",
          tenantId,
          headers: { "idempotency-key": crypto.randomUUID() },
          body: JSON.stringify({
            source: "file_upload",
            storageObjectId: yuklenen.storageObjectId,
            /** ⚠ Baz AÇIKÇA veriliyor: hangi hâlin üstüne yazdığımız tarihe geçmeli. */
            baseVersionId: versionId,
          }),
        })
      ).data as { id: string; docxImportLosses?: DocxIceAktarimKaybi[] };
    },
    onSuccess: async (yeni) => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id ?? "") });
      toast.success("Yeni versiyon oluşturuldu");
      /**
       * ⚠ KAYIP SESSİZ GEÇMİYOR (CLOS-1268 — kanon §38).
       *
       * Ölçüldü: karşı taraftan gelen izlenen değişiklikli dosyada silmeler metinden
       * düşüyor, eklemeler kabul edilmiş gibi giriyor. Kullanıcı bunu öğrenmezse karşı
       * tarafın ÖNERİSİNİ kabul edilmiş metin sanır.
       *
       * ⚠ Metin ŞEMADAN geliyor (kriter 6); arayüz kendi cümlesini uydurmuyor.
       *
       * ⚠ `toast.info` — `@closer/ui` bugün `warning` tonu taşımıyor ve bu kartta bileşen
       * kitaplığına ton eklemedik (packages/ui kendi süreci). Uyarının ağırlığını METİN
       * taşıyor; ton eklenirse buradaki çağrı tek satırda değişir.
       */
      for (const kayip of yeni.docxImportLosses ?? []) {
        toast.info(DOCX_ICE_AKTARIM_KAYIP_UYARILARI[kayip]);
      }
      void navigate(`/app/sozlesmeler/${id}/versiyonlar/${yeni.id}`);
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Versiyon kaydedilemedi"),
  });

  if (indirme.isPending) return <Skeleton lines={8} />;
  if (indirme.isError) {
    return (
      <StateView
        state={resolveErrorState(indirme.error, {
          idempotent: true,
          overrides: {
            notFound: {
              title: "Bu versiyonun DOCX'i yok",
              description:
                "Sözleşme metni henüz DOCX olarak üretilmemiş. Versiyon detayından materyalize edebilirsiniz.",
            },
          },
        })}
        onRetry={() => void indirme.refetch()}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title="Sözleşme metni"
        description={
          mod === "edit"
            ? "Belgeyi düzenleyebilirsiniz."
            : mod === "suggesting"
              ? "Değişiklik önerebilirsiniz."
              : "Bu belgeyi yalnız görüntüleyebilirsiniz."
        }
        actions={
          /** ⚠ Kaydet YALNIZ `contract.edit` ile: kapı sunucuda da aynı, burada gizleme kolaylık. */
          duzenleyebilir ? (
            <Button size="sm" loading={kaydet.isPending} onClick={() => kaydet.mutate()}>
              Yeni versiyon olarak kaydet
            </Button>
          ) : undefined
        }
      />

      {/*
        ⚠ BU METİN CLOS-1255 İLE DEĞİŞTİ — ve değişme sebebi kayda değer.

        Önceki hâli *"düzenlemeleriniz değişiklik olarak işaretlenmiyor"* diyordu ve o gün
        DOĞRUYDU: ADR-0070 ölçümü ücretsiz editör katmanının revizyon ÜRETMEDİĞİNİ
        göstermişti. Artık farkı Closer üretiyor (kaydetme anında baz versiyonla
        karşılaştırma), dolayısıyla eski cümle artık bir yalan olurdu.

        ⚠ Kullanıcıya "redline" DENMİYOR (kanon §28.2): cümle ne olduğunu anlatıyor, terimi
        değil — "Değişiklikleri Göster" versiyon sayfasındaki düğmenin adı.
      */}
      {duzenleyebilir ? (
        <p className="text-sm text-fg-muted" data-testid="redline-uyarisi">
          Kaydettiğinizde düzenlemeleriniz bir önceki versiyonla karşılaştırılır ve{" "}
          <strong>değişiklikler işaretlenmiş bir sürüm</strong> üretilir; karşı taraftan gelen
          değişiklik işaretleri de korunur.
        </p>
      ) : null}

      <Card>
        {/*
          ⚠ `Suspense` yedeği BOŞ DEĞİL: çekirdek indirilirken ekran boş kalsaydı kullanıcı
          sayfanın kırıldığını sanardı — paket büyük ve ilk açılışta beklemek normal.
        */}
        <Suspense fallback={<Skeleton lines={8} />}>
          {/*
            ⚠ `overflow-hidden` CANLIDA ölçülerek eklendi (CLOS-1256).

            Kap yalnız `h-[70vh]` idi; motor sayfaları o yüksekliği AŞINCA taşan tuval
            altındaki madde panelinin düğmelerini kapatıyordu — tarayıcı "buton
            `div.docx-page` tarafından örtülüyor" diyerek tıklamayı reddetti. Ekran
            doğru görünüyordu, tıklanamıyordu: yalnız gerçek tarayıcıda görülebilecek
            bir kusur.
          */}
          <div className="h-[70vh] w-full overflow-hidden" data-testid="docx-editor-kabi">
            <DocxEditor
              document={indirme.data}
              mode={mod}
              /**
               * ⚠ YAZAR OTURUMDAN: verilmezse editör kendi varsayılanını kullanır ve
               * belgede "kim değiştirdi" cevapsız kalır — kanonun izlenebilirlik şartı
               * orada kopar.
               */
              author={user?.name ?? undefined}
              /** ⚠ Tutamak `ref`'te tutuluyor: kaydetme onun `save()`'ini çağırıyor. */
              onReady={(ed: EditorTutamagi) => {
                editorRef.current = ed;
                /** ⚠ Hata YUTULMUYOR ama ekranı da düşürmüyor: panel yoksa editör çalışır. */
                void otomasyonHostuKur(ed)
                  .then(setOtomasyon)
                  .catch(() => setOtomasyon(null));
              }}
            />
          </div>
        </Suspense>
      </Card>

      {/*
        ⚠ MADDE PANELİ YALNIZ DÜZENLEME YETKİSİNDE (CLOS-1256). Görüntüleyen kullanıcıya
        madde aksiyonları göstermek, basınca reddedilecek düğmeler çizmek olurdu.
      */}
      {duzenleyebilir ? <ContractClausePanel host={otomasyon} /> : null}

      <Link
        className="text-sm font-medium text-primary hover:underline"
        to={`/app/sozlesmeler/${id}/versiyonlar/${versionId}`}
      >
        Versiyon detayına dön
      </Link>
    </div>
  );
}
